import { useEffect } from 'react';
import { Risco } from '../types';
import {
  SRPCS_SEVERIDADE,
  SRPCS_FREQUENCIA,
  SRPCS_POSSIBILIDADE,
  SRPCS_CATEGORIA,
  SRPCS_PLR,
  SRPCS_SIL,
  calcularDecisaoSRPCS,
  calcularSILPorPL,
} from '../../constants/srpcs-tables';
import { useI18n } from '@/contexts/i18n-hook';

interface SrpcsSectionProps {
  riscosEditados: Risco[];
  setRiscosEditados: (riscos: Risco[]) => void;
  exibirSRPCS: boolean;
  setExibirSRPCS: (exibir: boolean) => void;
}

export function SrpcsSection({ riscosEditados, setRiscosEditados, exibirSRPCS, setExibirSRPCS }: SrpcsSectionProps) {
  const { t } = useI18n();
  const handleAtualizarSRPCS = (
    index: number,
    campo: 'severidadeSRPCS' | 'frequenciaSRPCS' | 'possibilidadeSRPCS' | 'categoriaSeguranca' | 'plr' | 'sil',
    valor: string
  ) => {
    const novosRiscos = [...riscosEditados];
    const risco = novosRiscos[index];

    if (campo === 'severidadeSRPCS') {
      risco.severidadeSRPCS = valor as 'S1' | 'S2' | undefined;
    } else if (campo === 'frequenciaSRPCS') {
      risco.frequenciaSRPCS = valor as 'F1' | 'F2' | undefined;
    } else if (campo === 'possibilidadeSRPCS') {
      risco.possibilidadeSRPCS = valor as 'P1' | 'P2' | undefined;
    } else if (campo === 'categoriaSeguranca') {
      risco.categoriaSeguranca = valor as 'B' | '1' | '2' | '3' | '4' | undefined;
    } else if (campo === 'plr') {
      risco.plr = valor as 'a' | 'b' | 'c' | 'd' | 'e' | undefined;
    } else if (campo === 'sil') {
      risco.sil = valor as '1' | '2' | '3' | '4' | undefined;
    }

    setRiscosEditados(novosRiscos);
  };

  // Usar o primeiro risco para os valores SRP/CS (já que são por parte)
  const primeiroRisco = riscosEditados[0] || {};

  // Calcular automaticamente PLr e Categoria quando S, F e P forem selecionados
  useEffect(() => {
    if (
      primeiroRisco.severidadeSRPCS &&
      primeiroRisco.frequenciaSRPCS &&
      primeiroRisco.possibilidadeSRPCS &&
      riscosEditados.length > 0
    ) {
      const decisao = calcularDecisaoSRPCS(
        primeiroRisco.severidadeSRPCS,
        primeiroRisco.frequenciaSRPCS,
        primeiroRisco.possibilidadeSRPCS
      );

      if (decisao) {
        const novosRiscos = [...riscosEditados];
        const risco = novosRiscos[0];
        let precisaAtualizar = false;

        // Atualizar PLr apenas se ainda não foi definido manualmente ou se mudou
        if (!risco.plr || risco.plr !== decisao.plr) {
          risco.plr = decisao.plr;
          precisaAtualizar = true;
        }

        // Atualizar Categoria apenas se ainda não foi definida manualmente ou se não está nas possíveis
        if (!risco.categoriaSeguranca || !decisao.categoriasPossiveis.includes(risco.categoriaSeguranca as 'B' | '1' | '2' | '3' | '4')) {
          // Se a categoria atual não está nas possíveis, definir a primeira possível
          risco.categoriaSeguranca = decisao.categoriasPossiveis[0];
          precisaAtualizar = true;
        }

        // Calcular SIL baseado no PLr
        const silCalculado = calcularSILPorPL(decisao.plr);
        if (silCalculado && (!risco.sil || risco.sil !== silCalculado)) {
          risco.sil = silCalculado;
          precisaAtualizar = true;
        } else if (!silCalculado && risco.sil) {
          // Se PL a, remover SIL (não tem equivalente)
          risco.sil = undefined;
          precisaAtualizar = true;
        }

        if (precisaAtualizar) {
          setRiscosEditados(novosRiscos);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    primeiroRisco.severidadeSRPCS,
    primeiroRisco.frequenciaSRPCS,
    primeiroRisco.possibilidadeSRPCS,
  ]);

  // Obter decisão atual para exibir informações
  const decisaoAtual = calcularDecisaoSRPCS(
    primeiroRisco.severidadeSRPCS,
    primeiroRisco.frequenciaSRPCS,
    primeiroRisco.possibilidadeSRPCS
  );

  // Calcular SIL baseado no PLr atual
  const silCalculado = calcularSILPorPL(primeiroRisco.plr);

  return (
    <div className="space-y-4">
      {/* Checkbox para exibir/ocultar SRP/CS no documento */}
      <div className="flex items-center space-x-2 mb-4 p-3 bg-purple-50 rounded-lg border border-purple-200">
        <input
          type="checkbox"
          id="exibirSRPCS"
          checked={exibirSRPCS}
          onChange={(e) => setExibirSRPCS(e.target.checked)}
          className="h-4 w-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
        />
        <label htmlFor="exibirSRPCS" className="text-sm font-medium text-gray-700">
          {t('apreciacaoModais.editarParte.srpcs.exibirRotulo')}
        </label>
        {!exibirSRPCS && (
          <span className="text-xs text-orange-600 ml-2">
            {t('apreciacaoModais.editarParte.srpcs.exibirAviso')}
          </span>
        )}
      </div>
      <h2 className="text-lg font-semibold text-gray-900 mb-3">
        {t('apreciacaoModais.editarParte.srpcs.titulo')}
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Severidade */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('apreciacaoModais.editarParte.srpcs.severidadeRotulo')}
          </label>
          <select
            value={primeiroRisco.severidadeSRPCS || ''}
            onChange={(e) => handleAtualizarSRPCS(0, 'severidadeSRPCS', e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
          >
            <option value="">{t('comum.acao.selecioneOpcao')}</option>
            {SRPCS_SEVERIDADE.map((item) => (
              <option key={item.codigo} value={item.codigo}>
                {item.codigo} - {item.descricao}
              </option>
            ))}
          </select>
        </div>

        {/* Frequência */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('apreciacaoModais.editarParte.srpcs.frequenciaRotulo')}
          </label>
          <select
            value={primeiroRisco.frequenciaSRPCS || ''}
            onChange={(e) => handleAtualizarSRPCS(0, 'frequenciaSRPCS', e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
          >
            <option value="">{t('comum.acao.selecioneOpcao')}</option>
            {SRPCS_FREQUENCIA.map((item) => (
              <option key={item.codigo} value={item.codigo}>
                {item.codigo} - {item.descricao}
              </option>
            ))}
          </select>
        </div>

        {/* Possibilidade */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('apreciacaoModais.editarParte.srpcs.possibilidadeRotulo')}
          </label>
          <select
            value={primeiroRisco.possibilidadeSRPCS || ''}
            onChange={(e) => handleAtualizarSRPCS(0, 'possibilidadeSRPCS', e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
          >
            <option value="">{t('comum.acao.selecioneOpcao')}</option>
            {SRPCS_POSSIBILIDADE.map((item) => (
              <option key={item.codigo} value={item.codigo}>
                {item.codigo} - {item.descricao}
              </option>
            ))}
          </select>
        </div>

        {/* Categoria */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('apreciacaoModais.editarParte.srpcs.categoriaRotulo')}
            {decisaoAtual && (
              <span className="ml-2 text-xs text-gray-500">
                {t('apreciacaoModais.editarParte.srpcs.categoriasPossiveis', { lista: decisaoAtual.categoriasPossiveis.join(', ') })}
              </span>
            )}
          </label>
          <select
            value={primeiroRisco.categoriaSeguranca || ''}
            onChange={(e) => handleAtualizarSRPCS(0, 'categoriaSeguranca', e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
          >
            <option value="">{t('comum.acao.selecioneOpcao')}</option>
            {SRPCS_CATEGORIA.map((item) => {
              const isPossivel = decisaoAtual?.categoriasPossiveis.includes(item.codigo as 'B' | '1' | '2' | '3' | '4');
              return (
                <option
                  key={item.codigo}
                  value={item.codigo}
                  disabled={!!decisaoAtual && !isPossivel}
                  style={decisaoAtual && !isPossivel ? { color: '#999' } : {}}
                >
                  {item.codigo} - {item.descricao}
                  {decisaoAtual && !isPossivel ? t('apreciacaoModais.editarParte.srpcs.naoRecomendado') : ''}
                </option>
              );
            })}
          </select>
        </div>

        {/* PLr */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('apreciacaoModais.editarParte.srpcs.plrRotulo')}
            {decisaoAtual && (
              <span className="ml-2 text-xs text-blue-600 font-semibold">
                {t('apreciacaoModais.editarParte.srpcs.plrCalculado', { plr: decisaoAtual.plr.toUpperCase() })}
              </span>
            )}
          </label>
          <select
            value={primeiroRisco.plr || ''}
            onChange={(e) => handleAtualizarSRPCS(0, 'plr', e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-gray-50"
            disabled={!!decisaoAtual}
            title={decisaoAtual ? t('apreciacaoModais.editarParte.srpcs.plrTitle') : ''}
          >
            <option value="">{t('comum.acao.selecioneOpcao')}</option>
            {SRPCS_PLR.map((item) => (
              <option key={item.codigo} value={item.codigo}>
                PLr {item.codigo.toUpperCase()} - {item.descricao}
              </option>
            ))}
          </select>
          {decisaoAtual?.observacao && (
            <p className="mt-1 text-xs text-amber-600 italic">{decisaoAtual.observacao}</p>
          )}
        </div>

        {/* SIL */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('apreciacaoModais.editarParte.srpcs.silRotulo')}
            {silCalculado && (
              <span className="ml-2 text-xs text-blue-600 font-semibold">
                {t('apreciacaoModais.editarParte.srpcs.silCalculado', { sil: silCalculado })}
              </span>
            )}
            {primeiroRisco.plr === 'a' && (
              <span className="ml-2 text-xs text-gray-500">
                {t('apreciacaoModais.editarParte.srpcs.silSemEquivalente')}
              </span>
            )}
          </label>
          <select
            value={primeiroRisco.sil || ''}
            onChange={(e) => handleAtualizarSRPCS(0, 'sil', e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-gray-50"
            disabled={!!silCalculado}
            title={silCalculado ? t('apreciacaoModais.editarParte.srpcs.silTitle') : primeiroRisco.plr === 'a' ? t('apreciacaoModais.editarParte.srpcs.silTitlePlA') : ''}
          >
            <option value="">{t('comum.acao.selecioneOpcao')}</option>
            {SRPCS_SIL.map((item) => (
              <option key={item.codigo} value={item.codigo}>
                SIL {item.codigo} - {item.descricao}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}

